// In-page tab selection that survives back-navigation.
//
// The defect this exists to remove: every tabbed surface held its active tab in
// `useState`, so opening a row's detail and pressing Back remounted the list at
// the FIRST tab — the user was returned to a screen they had never been on.
// Component state cannot survive an unmount; the location can, so the tab
// belongs in the location.
//
// `replace: true` is the whole design, not an optimisation: pushing would make
// every tab click its own history entry, and Back from a detail would then walk
// the user through their own tab switches one at a time instead of returning
// them to the list. Replacing keeps ONE entry per visit whose URL happens to
// carry the current tab — which is exactly the state Back restores.
//
// The pattern is not new here: employee-list-content.tsx has driven its
// lifecycle tabs off `?lifecycle_state=` all along. This is that hook, extracted
// so the remaining surfaces read one implementation rather than eight copies.
//
// A tab is also a deep link now: `/me/benefits?tab=enrollments` is shareable and
// survives a reload. Unknown / hand-edited values fall back to `fallback`
// instead of rendering an empty panel.

import { useFilterParam } from "./use-url-filters";

export function useTabParam<T extends string>(
  paramName: string,
  values: readonly T[],
  fallback: T,
): readonly [T, (next: string) => void] {
  // A tab is a filter param with a closed value set: the write rules (absent
  // means fallback, replace never push) are shared, only the read is narrowed.
  const [raw, setActive] = useFilterParam(paramName, fallback);
  // The one cast lives HERE, where the membership test that justifies it is
  // visible. Generic in T so a caller with a union type keeps that union on the
  // READ side instead of casting a widened string back at every call site. The
  // WRITE side stays `string`: the setter is handed straight to Tabs/FilterPill
  // onChange handlers, which are typed to emit a plain string, and an unknown
  // value written here is narrowed on the next read anyway.
  const active = (values as readonly string[]).includes(raw) ? (raw as T) : fallback;

  return [active, setActive] as const;
}
